import { RESPONSE_MODES, DIMENSIONS, STATUSES, ROUNDS, OPTION_MODES, type Section } from './constants';

export interface QuestionFormState {
  section_id: string;
  round: number;
  dimension: string;          // '' when not active
  weight: number;
  response_mode: string;
  legacy_q_number: string;    // identity — create-only
  main_number: string;        // identity — create-only (kept as string for the input)
  sub_number: string;
  question_text: string;
  question_text_es: string;
  status: string;
  question_order: string;     // optional; '' = auto
  show_summary_after: boolean;
  is_suggested: boolean;
}

export const emptyQuestionForm: QuestionFormState = {
  section_id: '', round: 1, dimension: '', weight: 3, response_mode: 'single_select',
  legacy_q_number: '', main_number: '', sub_number: '', question_text: '', question_text_es: '',
  status: 'active', question_order: '', show_summary_after: false, is_suggested: false,
};

/** Returns a human error string if invalid, else null (mirrors backend validateQuestionWrite). */
export function validateQuestionForm(f: QuestionFormState, mode: 'create' | 'edit'): string | null {
  if (!f.section_id) return 'Section is required';
  if (!ROUNDS.includes(f.round as 1 | 2 | 3)) return 'Round must be 1, 2 or 3';
  if (!f.question_text.trim()) return 'Question text is required';
  if (!RESPONSE_MODES.includes(f.response_mode as never)) return 'Response mode is required';
  if (f.weight < 1 || f.weight > 5) return 'Weight must be 1–5';
  if (f.status === 'active' && !f.dimension) return 'Dimension is required when status is active';
  if (mode === 'create') {
    if (!f.legacy_q_number.trim()) return 'Question code (legacy_q_number) is required';
    if (f.main_number === '' || Number.isNaN(Number(f.main_number))) return 'Main number is required';
  }
  return null;
}

const inputCls = 'w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30';

export function QuestionForm({
  value, onChange, sections, mode,
}: {
  value: QuestionFormState;
  onChange: (next: QuestionFormState) => void;
  sections: Section[];
  mode: 'create' | 'edit';
}) {
  const set = <K extends keyof QuestionFormState>(k: K, v: QuestionFormState[K]) => onChange({ ...value, [k]: v });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Section <span className="text-destructive">*</span></label>
          <select value={value.section_id} onChange={(e) => set('section_id', e.target.value)} className={inputCls}>
            <option value="">Select a section…</option>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Round <span className="text-destructive">*</span></label>
          <select value={value.round} onChange={(e) => set('round', Number(e.target.value))} className={inputCls}>
            {ROUNDS.map((r) => <option key={r} value={r}>Round {r}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Question text (EN) <span className="text-destructive">*</span></label>
        <textarea rows={2} value={value.question_text} onChange={(e) => set('question_text', e.target.value)}
          className={`${inputCls} resize-none`} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Question text (ES)</label>
        <textarea rows={2} value={value.question_text_es} onChange={(e) => set('question_text_es', e.target.value)}
          placeholder="Optional Spanish translation" className={`${inputCls} resize-none`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Response mode <span className="text-destructive">*</span></label>
          <select value={value.response_mode} onChange={(e) => set('response_mode', e.target.value)} className={inputCls}>
            {RESPONSE_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {OPTION_MODES.includes(value.response_mode as never) && (
            <p className="text-xs text-muted-foreground mt-1">Uses options (below).</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Dimension {value.status === 'active' && <span className="text-destructive">*</span>}
          </label>
          <select value={value.dimension} onChange={(e) => set('dimension', e.target.value)} className={inputCls}>
            <option value="">—</option>
            {DIMENSIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Weight (1–5)</label>
          <input type="number" min={1} max={5} value={value.weight}
            onChange={(e) => set('weight', Math.min(5, Math.max(1, Number(e.target.value) || 1)))} className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select value={value.status} onChange={(e) => set('status', e.target.value)} className={inputCls}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Display order</label>
          <input type="number" min={1} value={value.question_order} placeholder="Auto (append)"
            onChange={(e) => set('question_order', e.target.value)} className={inputCls} />
        </div>
        <div className="flex flex-col gap-2 justify-end pb-1">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={value.show_summary_after} onChange={(e) => set('show_summary_after', e.target.checked)} />
            Show summary after
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={value.is_suggested} onChange={(e) => set('is_suggested', e.target.checked)} />
            Suggested
          </label>
        </div>
      </div>

      {/* Identity fields — editable only on create */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Question code {mode === 'create' && <span className="text-destructive">*</span>}</label>
          <input type="text" value={value.legacy_q_number} disabled={mode === 'edit'}
            onChange={(e) => set('legacy_q_number', e.target.value)}
            placeholder="e.g. 1-A" className={`${inputCls} ${mode === 'edit' ? 'opacity-60' : ''}`} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Main number {mode === 'create' && <span className="text-destructive">*</span>}</label>
          <input type="number" value={value.main_number} disabled={mode === 'edit'}
            onChange={(e) => set('main_number', e.target.value)} className={`${inputCls} ${mode === 'edit' ? 'opacity-60' : ''}`} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Sub number</label>
          <input type="number" value={value.sub_number} disabled={mode === 'edit'}
            onChange={(e) => set('sub_number', e.target.value)} placeholder="Optional" className={`${inputCls} ${mode === 'edit' ? 'opacity-60' : ''}`} />
        </div>
      </div>
      {mode === 'edit' && <p className="text-xs text-muted-foreground -mt-2">Identity fields (code / main / sub number) are immutable after creation.</p>}
    </div>
  );
}

/** Map the form state → the backend create/update body (drops empty optionals). */
export function formToBody(f: QuestionFormState, mode: 'create' | 'edit'): Record<string, unknown> {
  const body: Record<string, unknown> = {
    section_id: f.section_id,
    round: f.round,
    question_text: f.question_text.trim(),
    question_text_es: f.question_text_es.trim() || null,
    response_mode: f.response_mode,
    weight: f.weight,
    dimension: f.dimension || null,
    status: f.status,
    show_summary_after: f.show_summary_after,
    is_suggested: f.is_suggested,
  };
  if (f.question_order !== '') body.question_order = Number(f.question_order);
  if (mode === 'create') {
    body.legacy_q_number = f.legacy_q_number.trim();
    body.main_number = Number(f.main_number);
    if (f.sub_number !== '') body.sub_number = Number(f.sub_number);
  }
  return body;
}
